import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  OrgChartData,
  Employee,
  Division,
  Department,
  DivisionKey,
  AddEmployeeParams,
  OrgFunction,
  Section,
  ExecutiveFunction
} from '../types/orgchart.types';
import { OrgBoardOption, createEmptyOrgBoard, OrgBoardLanguage } from '../data/orgboards';
import { useAuth, db } from './AuthContext';

// Import Firebase instances from @platform/core
import { functions } from '@platform/core';

type LayoutDirection = 'top' | 'left';
type ViewMode = 'orgboard' | 'personnel';

interface OrgDataContextType {
  // Data
  orgData: OrgChartData;
  employees: Employee[];
  divisions: Record<DivisionKey, Division>;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Org Board Selection
  availableOrgBoards: OrgBoardOption[];
  selectedOrgBoardId: string;
  selectOrgBoard: (id: string) => void;

  // UI State
  hiddenDivisions: Set<DivisionKey>;
  maxLevel: number;
  searchQuery: string;
  showVfp: boolean;
  highlightedEmployeeId: string | null;
  layout: LayoutDirection;
  viewMode: ViewMode;
  focusedDivision: DivisionKey | null;  // null = show all, otherwise show only this division
  focusedDepartment: number | null;     // null = show all departments, otherwise show only this department index

  // CRUD Actions for CopilotKit
  addEmployee: (params: AddEmployeeParams) => Employee;
  updateEmployee: (id: string, updates: Partial<Employee>) => Employee | null;
  removeEmployee: (id: string, reassignChildrenTo?: string) => boolean;
  updateDivision: (key: DivisionKey, updates: Partial<Division>) => void;
  updateDepartment: (divisionKey: DivisionKey, deptIndex: number, updates: { name?: string; vfp?: string; managerId?: string }) => void;
  updateCompanyVfp: (companyVfp: string) => void;

  // Department CRUD
  addDepartment: (divisionKey: DivisionKey, dept: { name: string; vfp?: string; number?: number; managerId?: string }) => void;
  removeDepartment: (divisionKey: DivisionKey, deptIndex: number) => void;

  // Section CRUD (new 4-level hierarchy: Division → Department → Section → Function)
  addSection: (divisionKey: DivisionKey, deptIndex: number, section: { name: string; managerId?: string }) => void;
  updateSection: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, updates: Partial<Section>) => void;
  removeSection: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number) => void;
  reorderSection: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, direction: 'up' | 'down') => void;

  // Function CRUD (4-level hierarchy: Division → Department → Section → Function)
  updateFunction: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, funcIndex: number, updates: Partial<OrgFunction>) => void;
  addFunction: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, func: OrgFunction) => void;
  removeFunction: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, funcIndex: number) => void;
  reorderFunction: (divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, funcIndex: number, direction: 'up' | 'down') => void;
  moveFunction: (fromDivKey: DivisionKey, fromDeptIdx: number, fromSectionIdx: number, funcIdx: number, toDivKey: DivisionKey, toDeptIdx: number, toSectionIdx: number) => void;
  updateAssignments: (functionId: string, employeeIds: string[]) => void;

  // Executive Function CRUD (CEO, C-level positions)
  updateExecutiveFunction: (execId: string, updates: Partial<ExecutiveFunction>) => void;
  addExecutiveFunction: (exec: ExecutiveFunction) => void;
  removeExecutiveFunction: (execId: string) => void;
  reorderExecutiveFunction: (execId: string, direction: 'up' | 'down') => void;

  // UI Actions
  toggleDivision: (key: DivisionKey) => void;
  setMaxLevel: (level: number) => void;
  setSearchQuery: (query: string) => void;
  toggleVfp: () => void;
  toggleLayout: () => void;
  toggleViewMode: () => void;
  setHighlightedEmployeeId: (id: string | null) => void;
  setFocusedDivision: (key: DivisionKey | null) => void;
  setFocusedDepartment: (index: number | null) => void;
  expandAll: () => void;
  collapseAll: () => void;

  // Utilities
  getEmployeeById: (id: string) => Employee | undefined;
  getEmployeesByDivision: (division: DivisionKey) => Employee[];
  getSubordinates: (id: string) => Employee[];
  getFilteredEmployees: () => Employee[];

  // Import/Export
  exportToJson: () => void;
  importFromJson: (file: File) => Promise<boolean>;
  generateFromPdf: (file: File) => Promise<boolean>;
  resetToDefault: (language?: OrgBoardLanguage) => void;
}

const OrgDataContext = createContext<OrgDataContextType | null>(null);

// Generate unique ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// WISE Standard division colors
const WISE_COLORS: Record<string, string> = {
  hr: '#e1c005',        // Division 1 - Establishment (Gold)
  sales: '#c799ff',     // Division 2 - Marketing & Sales (Purple)
  finance: '#ee9696',   // Division 3 - Treasury (Pink)
  operations: '#6aba6a', // Division 4 - Delivery (Light Green)
  quality: '#adadad',   // Division 5 - Qualifications (Gray)
  bizdev: '#e9ec36',    // Division 6 - Public (Yellow)
  executive: '#87CEEB', // Division 7 - Executive (Sky blue)
};

// Old colors to migrate to new standard
const OLD_COLORS: Record<string, string> = {
  '#90d590': '#6aba6a',
  '#98fb98': '#6aba6a',
  '#4a964a': '#6aba6a', // Migrate old dark green to lighter
  '#87ceeb': '#87CEEB',
  '#7fdbdb': '#87CEEB',
  '#4fbde8': '#87CEEB',
  '#fff3b0': '#e9ec36',
  '#ffff99': '#e9ec36',
  '#ffb6c1': '#ee9696',
  '#c0c0c0': '#adadad',
  '#d8bfd8': '#c799ff',
  '#dda0dd': '#c799ff',
  '#f5c542': '#e1c005',
  '#ffd700': '#e1c005',
};

// Migrate legacy data (departments with functions) to 4-level hierarchy (with sections)
// Also fixes division colors to WISE standard
const migrateToSections = (data: OrgChartData): OrgChartData => {
  const migratedDivisions: Record<string, any> = {};

  for (const [divKey, div] of Object.entries(data.divisions)) {
    const migratedDepartments = div.departments?.map((dept: any) => {
      // If department already has sections array with items, keep them
      if (dept.sections && dept.sections.length > 0) {
        return {
          ...dept,
          sections: dept.sections.map((s: any) => ({
            ...s,
            functions: s.functions || []
          }))
        };
      }

      // Migrate: wrap existing functions in a default section
      const legacyFunctions = dept.functions || [];
      const defaultSection = {
        id: `${divKey}-d${dept.number}-s0`,
        name: 'מדור',
        functions: legacyFunctions
      };

      // Remove legacy functions field, add sections
      const { functions, ...deptWithoutFunctions } = dept;
      return {
        ...deptWithoutFunctions,
        sections: [defaultSection]
      };
    }) || [];

    // Fix division color to WISE standard
    let newColor = div.color;
    if (WISE_COLORS[divKey]) {
      newColor = WISE_COLORS[divKey];
    } else if (newColor && OLD_COLORS[newColor.toLowerCase()]) {
      newColor = OLD_COLORS[newColor.toLowerCase()];
    }

    migratedDivisions[divKey] = {
      ...div,
      color: newColor,
      departments: migratedDepartments
    };
  }

  return {
    company: data.company,
    companyVfp: data.companyVfp,
    divisions: migratedDivisions as OrgChartData['divisions'],
    employees: data.employees || [],
    executiveFunctions: data.executiveFunctions || [],
    assignments: data.assignments || {},
  };
};

// Load org board data from Firestore
const loadFromFirestore = async (organizationId: string): Promise<OrgChartData | null> => {
  try {
    const docRef = doc(db, 'orgboards', organizationId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      // Migrate legacy data to 4-level hierarchy if needed
      return migrateToSections(data.data as OrgChartData);
    }
  } catch (e) {
    console.error('Failed to load from Firestore:', e);
  }
  return null;
};

// Remove undefined values recursively (Firestore doesn't accept undefined)
const sanitizeForFirestore = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirestore(item));
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = sanitizeForFirestore(value);
      }
    }
    return cleaned;
  }
  return obj;
};

// Save org board data to Firestore
const saveToFirestore = async (organizationId: string, data: OrgChartData, userId: string) => {
  try {
    const docRef = doc(db, 'orgboards', organizationId);
    const sanitizedData = sanitizeForFirestore(data);
    await setDoc(docRef, {
      organizationId,
      data: sanitizedData,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
    console.log('Saved to Firestore:', organizationId);
  } catch (e) {
    console.error('Failed to save to Firestore:', e);
  }
};

export const OrgDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, organization, organizations, canEdit, isAdmin, selectOrganization, updateOrganizationSettings } = useAuth();

  const [selectedOrgBoardId, setSelectedOrgBoardId] = useState<string>('');
  const [orgData, setOrgData] = useState<OrgChartData>(createEmptyOrgBoard());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false); // Prevent auto-save of initial/empty data

  // Load from Firestore when organization changes
  useEffect(() => {
    const loadData = async () => {
      setHasUserMadeChanges(false); // Reset when loading new data

      if (!organization?.id) {
        // No organization selected - show empty template (user will be prompted to select/create org)
        setOrgData(createEmptyOrgBoard(undefined, 'he'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const stored = await loadFromFirestore(organization.id);
      if (stored) {
        setOrgData(stored);
      } else {
        // Initialize with empty template for new organization
        // Pass companyVfp from organization if it was set during creation
        setOrgData(createEmptyOrgBoard(organization.name, organization.language || 'he', organization.companyVfp));
      }
      setIsLoading(false);
    };
    loadData();
  }, [organization?.id, organization?.name]);

  // Sync selectedOrgBoardId with organization.id
  // This ensures the dropdown reflects the currently loaded organization
  useEffect(() => {
    if (organization?.id && organization.id !== selectedOrgBoardId) {
      setSelectedOrgBoardId(organization.id);
    }
  }, [organization?.id, selectedOrgBoardId]);

  // Auto-select first organization for admins who don't have one selected
  // This ensures admins can start editing right away
  useEffect(() => {
    if (isAdmin && !organization?.id && organizations.length > 0) {
      console.log('Auto-selecting first organization for admin:', organizations[0].id);
      selectOrganization(organizations[0].id);
    }
  }, [isAdmin, organization?.id, organizations, selectOrganization]);

  const [hiddenDivisions, setHiddenDivisions] = useState<Set<DivisionKey>>(new Set());
  const [maxLevel, setMaxLevel] = useState(2);
  const [searchQuery, setSearchQuery] = useState('');
  const [showVfp, setShowVfp] = useState(false); // Off by default for clean Org Board view
  const [highlightedEmployeeId, setHighlightedEmployeeId] = useState<string | null>(null);
  const [layout, setLayout] = useState<LayoutDirection>('top');
  const [viewMode, setViewMode] = useState<ViewMode>('orgboard'); // Default to org board view
  const [focusedDivision, setFocusedDivisionState] = useState<DivisionKey | null>(null); // null = show all
  const [focusedDepartment, setFocusedDepartment] = useState<number | null>(null); // null = show all departments

  // When division changes, reset department filter
  const setFocusedDivision = useCallback((key: DivisionKey | null) => {
    setFocusedDivisionState(key);
    setFocusedDepartment(null); // Reset department when division changes
  }, []);

  // Compute available org boards based on auth state
  // - For platform admins: show all Firebase organizations
  // - For org users: show only their organization (no dropdown needed)
  // - For unauthenticated: show nothing (they'll be redirected to login)
  const availableOrgBoards: OrgBoardOption[] = useMemo(() => {
    if (isAdmin && organizations.length > 0) {
      // Platform admin: show all Firebase organizations
      return organizations.map(org => ({
        id: org.id,
        name: org.name,
        data: createEmptyOrgBoard(org.name, org.language || 'he'), // Placeholder, actual data loaded from Firebase
      }));
    }
    // Non-admin users: show only their organization
    if (organization) {
      return [{
        id: organization.id,
        name: organization.name,
        data: createEmptyOrgBoard(organization.name, organization.language || 'he'),
      }];
    }
    // No organization assigned - return empty (user shouldn't see templates)
    return [];
  }, [isAdmin, organizations, organization]);

  // Auto-save to Firestore whenever orgData changes (debounced)
  // IMPORTANT: Only saves after user has made actual changes, not on initial load
  useEffect(() => {
    // Debug: show which conditions are passing/failing
    const conditions = {
      isLoading: isLoading ? 'SKIP (loading)' : 'OK',
      hasUserMadeChanges: hasUserMadeChanges ? 'OK' : 'SKIP (no user changes yet)',
      orgId: organization?.id || 'SKIP (no org)',
      userId: user?.id || 'SKIP (no user)',
      canEdit: canEdit ? 'OK' : 'SKIP (no edit permission)'
    };
    console.log('Auto-save check:', conditions);

    if (isLoading) return; // Don't save while loading
    if (!hasUserMadeChanges) return; // Don't save until user makes actual changes
    if (!organization?.id) return; // No organization to save to
    if (!user?.id) return; // No user logged in
    if (!canEdit) return; // User doesn't have edit permissions

    const saveData = async () => {
      setIsSaving(true);
      await saveToFirestore(organization.id, orgData, user.id);
      setIsSaving(false);
    };

    // Debounce: wait 1 second after last change before saving
    const timeoutId = setTimeout(saveData, 1000);
    return () => clearTimeout(timeoutId);
  }, [orgData, organization?.id, user?.id, canEdit, isLoading, hasUserMadeChanges]);

  // Select a different org board (admin only - regular users can't switch orgs)
  const selectOrgBoard = useCallback(async (id: string) => {
    // Only platform admins can switch between organizations
    if (isAdmin && organizations.length > 0) {
      const firebaseOrg = organizations.find(org => org.id === id);
      if (firebaseOrg) {
        // Use AuthContext to switch organization
        // This will trigger the useEffect that loads from Firebase
        await selectOrganization(id);
        setSelectedOrgBoardId(id);
        setHiddenDivisions(new Set());
        return;
      }
    }
    // Non-admin users can't switch orgs - do nothing
  }, [isAdmin, organizations, selectOrganization]);

  // Helper to mark that user has made changes (enables auto-save)
  const markAsChanged = useCallback(() => {
    setHasUserMadeChanges(true);
  }, []);

  // CRUD Operations
  const addEmployee = useCallback((params: AddEmployeeParams): Employee => {
    const { name, division, deptNum, parentId, vfp, email, phone } = params;

    // Build employee object
    const newEmployee: Employee = {
      id: generateId(),
      name,
    };

    // Add optional fields if they have values (for backward compatibility)
    if (division) newEmployee.division = division;
    if (parentId) newEmployee.pid = parentId;
    if (deptNum !== undefined) newEmployee.deptNum = deptNum;
    if (vfp) newEmployee.vfp = vfp;
    if (email) newEmployee.email = email;
    if (phone) newEmployee.phone = phone;

    markAsChanged();
    setOrgData(prev => {
      // Get division number from current state if division provided
      if (division) {
        newEmployee.divNum = prev.divisions[division]?.number || 0;
      }
      return {
        ...prev,
        employees: [...prev.employees, newEmployee]
      };
    });

    return newEmployee;
  }, []);

  const updateEmployee = useCallback((id: string, updates: Partial<Employee>): Employee | null => {
    let updatedEmployee: Employee | null = null;

    // Filter out undefined values from updates (Firestore doesn't accept undefined)
    const cleanUpdates: Partial<Employee> = {};
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined) {
        (cleanUpdates as any)[key] = value;
      }
    });

    markAsChanged();
    setOrgData(prev => {
      const index = prev.employees.findIndex(e => e.id === id);
      if (index === -1) return prev;

      // If division changed, update divNum too
      if (cleanUpdates.division && cleanUpdates.division !== prev.employees[index].division) {
        cleanUpdates.divNum = prev.divisions[cleanUpdates.division]?.number || 0;
      }

      updatedEmployee = { ...prev.employees[index], ...cleanUpdates };
      const newEmployees = [...prev.employees];
      newEmployees[index] = updatedEmployee;

      return { ...prev, employees: newEmployees };
    });

    return updatedEmployee;
  }, []);

  const removeEmployee = useCallback((id: string): boolean => {
    let success = false;

    markAsChanged();
    setOrgData(prev => {
      const employee = prev.employees.find(e => e.id === id);
      if (!employee) return prev;

      // Remove employee and clean up any assignments
      const newEmployees = prev.employees.filter(e => e.id !== id);

      // Remove employee from any assignments
      const newAssignments = { ...(prev.assignments || {}) };
      Object.keys(newAssignments).forEach(funcId => {
        newAssignments[funcId] = newAssignments[funcId].filter(empId => empId !== id);
        if (newAssignments[funcId].length === 0) {
          delete newAssignments[funcId];
        }
      });

      success = true;
      return { ...prev, employees: newEmployees, assignments: newAssignments };
    });

    return success;
  }, []);

  const updateDivision = useCallback((key: DivisionKey, updates: Partial<Division>) => {
    markAsChanged();
    // Use functional update to avoid stale closure issues
    setOrgData(prev => {
      const newDivisions = {
        ...prev.divisions,
        [key]: { ...prev.divisions[key], ...updates }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const updateDepartment = useCallback((divisionKey: DivisionKey, deptIndex: number, updates: { name?: string; vfp?: string; managerId?: string }) => {
    markAsChanged();
    // Use functional update to avoid stale closure issues
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division || !division.departments || deptIndex < 0 || deptIndex >= division.departments.length) {
        return prev;
      }

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...newDepartments[deptIndex], ...updates };

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const addDepartment = useCallback((divisionKey: DivisionKey, dept: { name: string; vfp?: string; number?: number; managerId?: string }) => {
    markAsChanged();
    // Use functional update to avoid stale closure issues
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division) return prev;

      // Calculate new department number
      const existingDepts = division.departments || [];
      const maxNum = existingDepts.reduce((max, d) => Math.max(max, d.number || 0), 0);
      const newDeptNumber = dept.number || maxNum + 1;

      const newDept: Department = {
        name: dept.name,
        vfp: dept.vfp || '',
        number: newDeptNumber,
        sections: [{
          id: `${divisionKey}-d${newDeptNumber}-s0`,
          name: 'מדור',
          functions: []
        }],
        ...(dept.managerId ? { managerId: dept.managerId } : {})
      };

      const newDepartments = [...existingDepts, newDept];

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const removeDepartment = useCallback((divisionKey: DivisionKey, deptIndex: number) => {
    markAsChanged();
    // Use functional update to avoid stale closure issues
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division || !division.departments || deptIndex < 0 || deptIndex >= division.departments.length) {
        return prev;
      }

      const newDepartments = division.departments.filter((_, i) => i !== deptIndex);

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  // Section CRUD Operations (4-level hierarchy: Division → Department → Section → Function)
  const addSection = useCallback((divisionKey: DivisionKey, deptIndex: number, section: { name: string; managerId?: string }) => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division || !division.departments || !division.departments[deptIndex]) return prev;

      const dept = division.departments[deptIndex];
      const existingSections = dept.sections || [];

      // Generate section ID: "divKey-d{deptNum}-s{sectionIndex}"
      const sectionId = `${divisionKey}-d${dept.number}-s${existingSections.length}`;

      const newSection: Section = {
        id: sectionId,
        name: section.name,
        functions: [],
        ...(section.managerId ? { managerId: section.managerId } : {})
      };

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = {
        ...dept,
        sections: [...existingSections, newSection]
      };

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const updateSection = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, updates: Partial<Section>) => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division || !division.departments || !division.departments[deptIndex]) return prev;

      const dept = division.departments[deptIndex];
      if (!dept.sections || !dept.sections[sectionIndex]) return prev;

      const newSections = [...dept.sections];
      newSections[sectionIndex] = { ...newSections[sectionIndex], ...updates };

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const removeSection = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number) => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division || !division.departments || !division.departments[deptIndex]) return prev;

      const dept = division.departments[deptIndex];
      if (!dept.sections || !dept.sections[sectionIndex]) return prev;

      const newSections = dept.sections.filter((_, i) => i !== sectionIndex);

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const reorderSection = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, direction: 'up' | 'down') => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division || !division.departments || !division.departments[deptIndex]) return prev;

      const dept = division.departments[deptIndex];
      if (!dept.sections || dept.sections.length < 2) return prev;

      const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
      if (newIndex < 0 || newIndex >= dept.sections.length) return prev;

      const newSections = [...dept.sections];
      [newSections[sectionIndex], newSections[newIndex]] = [newSections[newIndex], newSections[sectionIndex]];

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      const newDivisions = {
        ...prev.divisions,
        [divisionKey]: { ...division, departments: newDepartments }
      };
      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const updateCompanyVfp = useCallback((companyVfp: string) => {
    markAsChanged();
    setOrgData(prev => ({
      ...prev,
      companyVfp
    }));
  }, []);

  // Function CRUD Operations (4-level hierarchy: Division → Department → Section → Function)
  const updateFunction = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, funcIndex: number, updates: Partial<OrgFunction>) => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division?.departments?.[deptIndex]?.sections?.[sectionIndex]?.functions?.[funcIndex]) return prev;

      const dept = division.departments[deptIndex];
      const section = dept.sections[sectionIndex];
      const newFunctions = [...section.functions];
      newFunctions[funcIndex] = { ...newFunctions[funcIndex], ...updates };

      const newSections = [...dept.sections];
      newSections[sectionIndex] = { ...section, functions: newFunctions };

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      return {
        ...prev,
        divisions: {
          ...prev.divisions,
          [divisionKey]: { ...division, departments: newDepartments }
        }
      };
    });
  }, []);

  const addFunction = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, func: OrgFunction) => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division?.departments?.[deptIndex]?.sections?.[sectionIndex]) return prev;

      const dept = division.departments[deptIndex];
      const section = dept.sections[sectionIndex];

      // Generate function ID: "divKey-d{deptNum}-s{sectionIdx}-f{funcIdx}"
      const funcId = `${divisionKey}-d${dept.number}-s${sectionIndex}-f${section.functions.length}`;
      const newFunc = { ...func, id: funcId };

      const newFunctions = [...section.functions, newFunc];
      const newSections = [...dept.sections];
      newSections[sectionIndex] = { ...section, functions: newFunctions };

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      return {
        ...prev,
        divisions: {
          ...prev.divisions,
          [divisionKey]: { ...division, departments: newDepartments }
        }
      };
    });
  }, []);

  const removeFunction = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, funcIndex: number) => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division?.departments?.[deptIndex]?.sections?.[sectionIndex]?.functions?.[funcIndex]) return prev;

      const dept = division.departments[deptIndex];
      const section = dept.sections[sectionIndex];
      const newFunctions = section.functions.filter((_, i) => i !== funcIndex);

      const newSections = [...dept.sections];
      newSections[sectionIndex] = { ...section, functions: newFunctions };

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      return {
        ...prev,
        divisions: {
          ...prev.divisions,
          [divisionKey]: { ...division, departments: newDepartments }
        }
      };
    });
  }, []);

  const reorderFunction = useCallback((divisionKey: DivisionKey, deptIndex: number, sectionIndex: number, funcIndex: number, direction: 'up' | 'down') => {
    markAsChanged();
    setOrgData(prev => {
      const division = prev.divisions[divisionKey];
      if (!division?.departments?.[deptIndex]?.sections?.[sectionIndex]) return prev;

      const dept = division.departments[deptIndex];
      const section = dept.sections[sectionIndex];
      if (section.functions.length < 2) return prev;

      const newIndex = direction === 'up' ? funcIndex - 1 : funcIndex + 1;
      if (newIndex < 0 || newIndex >= section.functions.length) return prev;

      const newFunctions = [...section.functions];
      [newFunctions[funcIndex], newFunctions[newIndex]] = [newFunctions[newIndex], newFunctions[funcIndex]];

      const newSections = [...dept.sections];
      newSections[sectionIndex] = { ...section, functions: newFunctions };

      const newDepartments = [...division.departments];
      newDepartments[deptIndex] = { ...dept, sections: newSections };

      return {
        ...prev,
        divisions: {
          ...prev.divisions,
          [divisionKey]: { ...division, departments: newDepartments }
        }
      };
    });
  }, []);

  const moveFunction = useCallback((fromDivKey: DivisionKey, fromDeptIdx: number, fromSectionIdx: number, funcIdx: number, toDivKey: DivisionKey, toDeptIdx: number, toSectionIdx: number) => {
    markAsChanged();
    setOrgData(prev => {
      const fromDivision = prev.divisions[fromDivKey];
      const toDivision = prev.divisions[toDivKey];
      if (!fromDivision?.departments?.[fromDeptIdx]?.sections?.[fromSectionIdx]?.functions?.[funcIdx]) return prev;
      if (!toDivision?.departments?.[toDeptIdx]?.sections?.[toSectionIdx]) return prev;

      const fromDept = fromDivision.departments[fromDeptIdx];
      const fromSection = fromDept.sections[fromSectionIdx];
      const funcToMove = fromSection.functions[funcIdx];

      // Remove from source
      const newFromFunctions = fromSection.functions.filter((_, i) => i !== funcIdx);
      const newFromSections = [...fromDept.sections];
      newFromSections[fromSectionIdx] = { ...fromSection, functions: newFromFunctions };

      const newFromDepartments = [...fromDivision.departments];
      newFromDepartments[fromDeptIdx] = { ...fromDept, sections: newFromSections };

      // Determine target (may be same division)
      const newToDepartments = fromDivKey === toDivKey ? newFromDepartments : [...toDivision.departments];
      const targetDept = newToDepartments[toDeptIdx];
      const toSection = targetDept.sections[toSectionIdx];

      // Add to target
      const newToFunctions = [...toSection.functions, funcToMove];
      const newToSections = [...targetDept.sections];
      newToSections[toSectionIdx] = { ...toSection, functions: newToFunctions };
      newToDepartments[toDeptIdx] = { ...targetDept, sections: newToSections };

      const newDivisions = {
        ...prev.divisions,
        [fromDivKey]: { ...fromDivision, departments: newFromDepartments },
      };

      if (fromDivKey !== toDivKey) {
        newDivisions[toDivKey] = { ...toDivision, departments: newToDepartments };
      } else {
        newDivisions[fromDivKey] = { ...fromDivision, departments: newToDepartments };
      }

      return { ...prev, divisions: newDivisions };
    });
  }, []);

  const updateAssignments = useCallback((functionId: string, employeeIds: string[]) => {
    markAsChanged();
    setOrgData(prev => ({
      ...prev,
      assignments: {
        ...(prev.assignments || {}),
        [functionId]: employeeIds
      }
    }));
  }, []);

  // Executive Function CRUD Operations (CEO, C-level positions)
  const updateExecutiveFunction = useCallback((execId: string, updates: Partial<ExecutiveFunction>) => {
    markAsChanged();
    setOrgData(prev => {
      const execFunctions = prev.executiveFunctions || [];
      const index = execFunctions.findIndex(f => f.id === execId);
      if (index === -1) return prev;

      const newExecFunctions = [...execFunctions];
      newExecFunctions[index] = { ...newExecFunctions[index], ...updates };

      return { ...prev, executiveFunctions: newExecFunctions };
    });
  }, []);

  const addExecutiveFunction = useCallback((exec: ExecutiveFunction) => {
    markAsChanged();
    setOrgData(prev => ({
      ...prev,
      executiveFunctions: [...(prev.executiveFunctions || []), exec]
    }));
  }, []);

  const removeExecutiveFunction = useCallback((execId: string) => {
    markAsChanged();
    setOrgData(prev => {
      const newExecFunctions = (prev.executiveFunctions || []).filter(f => f.id !== execId);
      // Also remove any assignments to this function
      const newAssignments = { ...(prev.assignments || {}) };
      delete newAssignments[execId];
      return { ...prev, executiveFunctions: newExecFunctions, assignments: newAssignments };
    });
  }, []);

  const reorderExecutiveFunction = useCallback((execId: string, direction: 'up' | 'down') => {
    markAsChanged();
    setOrgData(prev => {
      const execFunctions = prev.executiveFunctions || [];
      // Filter out Owner and CEO - they shouldn't be reordered
      const cLevelFuncs = execFunctions.filter(f => f.id !== 'exec-owner' && f.id !== 'exec-ceo');
      const fixedFuncs = execFunctions.filter(f => f.id === 'exec-owner' || f.id === 'exec-ceo');

      const index = cLevelFuncs.findIndex(f => f.id === execId);
      if (index === -1) return prev;

      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= cLevelFuncs.length) return prev;

      // Swap positions
      const newCLevelFuncs = [...cLevelFuncs];
      [newCLevelFuncs[index], newCLevelFuncs[newIndex]] = [newCLevelFuncs[newIndex], newCLevelFuncs[index]];

      // Update sortOrder for all C-level functions
      newCLevelFuncs.forEach((func, idx) => {
        func.sortOrder = idx;
      });

      return { ...prev, executiveFunctions: [...fixedFuncs, ...newCLevelFuncs] };
    });
  }, []);

  // UI Actions
  const toggleDivision = useCallback((key: DivisionKey) => {
    if (key === 'executive') return; // Can't hide CEO

    setHiddenDivisions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const toggleVfp = useCallback(() => {
    setShowVfp(prev => !prev);
  }, []);

  const toggleLayout = useCallback(() => {
    setLayout(prev => prev === 'top' ? 'left' : 'top');
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'orgboard' ? 'personnel' : 'orgboard');
  }, []);

  const expandAll = useCallback(() => {
    setMaxLevel(10);
  }, []);

  const collapseAll = useCallback(() => {
    setMaxLevel(1);
  }, []);

  // Utilities
  const getEmployeeById = useCallback((id: string) => {
    return orgData.employees.find(e => e.id === id);
  }, [orgData.employees]);

  // Get employees by division (legacy - uses employee.division field)
  const getEmployeesByDivision = useCallback((division: DivisionKey) => {
    return orgData.employees.filter(e => e.division === division);
  }, [orgData.employees]);

  // Get subordinates by parent ID (legacy - uses employee.pid field)
  const getSubordinates = useCallback((id: string) => {
    return orgData.employees.filter(e => e.pid === id);
  }, [orgData.employees]);

  const getFilteredEmployees = useCallback(() => {
    // In the new model, employees are just people - use WiseBoardView for org board display
    // This returns all employees for the personnel view
    return orgData.employees;
  }, [orgData.employees]);

  // Export to JSON file (includes organization settings)
  const exportToJson = useCallback(() => {
    // Include organization settings (logo, emoji) in the export
    const exportData = {
      ...orgData,
      organizationSettings: {
        name: organization?.name,
        logo: organization?.logo,
        emoji: organization?.emoji,
      }
    };
    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `orgchart_${organization?.name || 'export'}_${Date.now()}.json`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  }, [orgData, organization]);

  // Import from JSON file (includes organization settings)
  const importFromJson = useCallback(async (file: File): Promise<boolean> => {
    try {
      const text = await file.text();
      const data = JSON.parse(text) as OrgChartData & { organizationSettings?: { name?: string; logo?: string; emoji?: string }; company?: string };

      // Basic validation - check for companyVfp (new format) or company (legacy)
      if ((!data.companyVfp && !data.company) || !data.divisions || !data.employees) {
        console.error('Invalid org chart data structure');
        return false;
      }

      // Normalize legacy 'company' field to 'companyVfp'
      if (data.company && !data.companyVfp) {
        data.companyVfp = data.company;
        delete data.company;
      }

      // Extract organization settings before setting org data
      const orgSettings = data.organizationSettings;

      // Remove organizationSettings from data before setting orgData
      const { organizationSettings, ...orgChartData } = data;

      markAsChanged();
      setOrgData(orgChartData as OrgChartData);

      // Restore organization settings (logo, emoji) if present
      if (orgSettings && (orgSettings.logo !== undefined || orgSettings.emoji !== undefined)) {
        try {
          await updateOrganizationSettings({
            logo: orgSettings.logo || '',
            emoji: orgSettings.emoji || '',
          });
        } catch (err) {
          console.warn('Failed to restore organization settings:', err);
        }
      }

      return true;
    } catch (e) {
      console.error('Failed to import JSON:', e);
      return false;
    }
  }, [markAsChanged, updateOrganizationSettings]);

  // Generate org board from PDF using AI
  const generateFromPdf = useCallback(async (file: File): Promise<boolean> => {
    try {
      console.log('generateFromPdf: Converting PDF to base64...');

      // Convert file to base64
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const pdfBase64 = btoa(binary);

      console.log('generateFromPdf: Calling Firebase function...');

      // Call the Firebase function with extended timeout (5 minutes)
      const generateOrgBoardFromPdf = httpsCallable<
        { pdfBase64: string; companyName?: string },
        { success: boolean; orgBoard?: OrgChartData; error?: string }
      >(functions, 'generateOrgBoardFromPdf', { timeout: 300000 }); // 5 minutes

      const result = await generateOrgBoardFromPdf({
        pdfBase64,
        companyName: organization?.name
      });

      console.log('generateFromPdf: Got response:', result.data);

      if (result.data.success && result.data.orgBoard) {
        // Validate the response
        const data = result.data.orgBoard;
        if (!data.divisions || !data.employees) {
          console.error('Invalid org chart data from AI');
          return false;
        }

        markAsChanged();
        setOrgData(data);
        console.log('generateFromPdf: Successfully updated org data');
        return true;
      } else {
        console.error('Failed to generate org board:', result.data.error);
        return false;
      }
    } catch (e) {
      console.error('Failed to generate from PDF:', e);
      return false;
    }
  }, [organization?.name, markAsChanged]);

  // Reset to empty WISE template (clears all data for this org)
  const resetToDefault = useCallback((language?: OrgBoardLanguage) => {
    // Reset to empty WISE template with organization name
    const companyName = organization?.name || (language === 'en' ? 'New Company' : 'חברה חדשה');
    const orgLanguage = language || organization?.language || 'he';
    markAsChanged();
    setOrgData(createEmptyOrgBoard(companyName, orgLanguage));
    // This will trigger auto-save to server with the empty template
  }, [organization?.name, organization?.language, markAsChanged]);

  const value = useMemo(() => ({
    orgData,
    employees: orgData.employees,
    divisions: orgData.divisions,
    isLoading,
    isSaving,
    availableOrgBoards,
    selectedOrgBoardId,
    selectOrgBoard,
    hiddenDivisions,
    maxLevel,
    searchQuery,
    showVfp,
    highlightedEmployeeId,
    layout,
    viewMode,
    focusedDivision,
    focusedDepartment,
    addEmployee,
    updateEmployee,
    removeEmployee,
    updateDivision,
    updateDepartment,
    addDepartment,
    removeDepartment,
    addSection,
    updateSection,
    removeSection,
    reorderSection,
    updateCompanyVfp,
    updateFunction,
    addFunction,
    removeFunction,
    reorderFunction,
    moveFunction,
    updateAssignments,
    updateExecutiveFunction,
    addExecutiveFunction,
    removeExecutiveFunction,
    reorderExecutiveFunction,
    toggleDivision,
    setMaxLevel,
    setSearchQuery,
    toggleVfp,
    toggleLayout,
    toggleViewMode,
    setHighlightedEmployeeId,
    setFocusedDivision,
    setFocusedDepartment,
    expandAll,
    collapseAll,
    getEmployeeById,
    getEmployeesByDivision,
    getSubordinates,
    getFilteredEmployees,
    exportToJson,
    importFromJson,
    generateFromPdf,
    resetToDefault,
  }), [
    orgData,
    isLoading,
    isSaving,
    availableOrgBoards,
    selectedOrgBoardId,
    selectOrgBoard,
    hiddenDivisions,
    maxLevel,
    searchQuery,
    showVfp,
    highlightedEmployeeId,
    layout,
    viewMode,
    focusedDivision,
    focusedDepartment,
    addEmployee,
    updateEmployee,
    removeEmployee,
    updateDivision,
    updateDepartment,
    addDepartment,
    removeDepartment,
    addSection,
    updateSection,
    removeSection,
    reorderSection,
    updateCompanyVfp,
    updateFunction,
    addFunction,
    removeFunction,
    reorderFunction,
    moveFunction,
    updateAssignments,
    updateExecutiveFunction,
    addExecutiveFunction,
    removeExecutiveFunction,
    reorderExecutiveFunction,
    toggleDivision,
    toggleVfp,
    toggleLayout,
    toggleViewMode,
    expandAll,
    collapseAll,
    getEmployeeById,
    getEmployeesByDivision,
    getSubordinates,
    getFilteredEmployees,
    exportToJson,
    importFromJson,
    generateFromPdf,
    resetToDefault
  ]);

  return (
    <OrgDataContext.Provider value={value}>
      {children}
    </OrgDataContext.Provider>
  );
};

export const useOrgData = () => {
  const context = useContext(OrgDataContext);
  if (!context) {
    throw new Error('useOrgData must be used within an OrgDataProvider');
  }
  return context;
};
